// Error monitoring (Sentry) for the admin app and the published player. The SDK is downloaded only when the
// server has a DSN configured (GET /api/config), so a viewer never loads it otherwise. Errors raised before it is
// ready are held and sent once it is, because a crash while the page starts is the one most worth knowing about.
//
// Every event carries the environment, which app it came from (admin or player) and whatever the page has said
// it is showing (demo_id, screen_id, ...: see setMonitoringTags). Query strings and hashes are cut from every URL
// sent, because demo links carry personalisation such as ?company=Acme&first_name=Sam. No user identity, IP
// address, cookies, session replays or performance traces are collected.

import type { Breadcrumb, ErrorEvent as SentryEvent } from "@sentry/browser";

type Sentry = typeof import("./sentry");

let sentry: Sentry | null = null;
let off = false;
const waiting: ((s: Sentry) => void)[] = [];
const tags: Record<string, string> = {};

function whenReady(run: (s: Sentry) => void) {
  if (sentry) run(sentry);
  else if (!off && waiting.length < 50) waiting.push(run);
}

const onEarlyError = (e: ErrorEvent) => whenReady((s) => s.captureException(e.error ?? new Error(e.message)));
const onEarlyRejection = (e: PromiseRejectionEvent) => whenReady((s) => s.captureException(e.reason));
if (typeof window !== "undefined") {
  window.addEventListener("error", onEarlyError);
  window.addEventListener("unhandledrejection", onEarlyRejection);
}
function stopEarlyCapture() {
  window.removeEventListener("error", onEarlyError);
  window.removeEventListener("unhandledrejection", onEarlyRejection);
}

/** "https://x/acme/tour?company=Acme#3" → "https://x/acme/tour". */
export const scrubUrl = (url: string) => url.replace(/[?#].*$/s, "");

/** Adds the app and page labels and strips anything personal from an event before it is sent. */
export function prepareEvent(event: SentryEvent, pageTags: Record<string, string>, app: string): SentryEvent {
  event.tags = { ...event.tags, app, ...pageTags };
  if (event.request) {
    if (event.request.url) event.request.url = scrubUrl(event.request.url);
    delete event.request.query_string;
    delete event.request.cookies;
    if (event.request.headers) delete event.request.headers.Referer;
  }
  return event;
}

export function prepareBreadcrumb(crumb: Breadcrumb): Breadcrumb {
  const data = crumb.data;
  if (data) for (const k of ["url", "from", "to"]) if (typeof data[k] === "string") data[k] = scrubUrl(data[k]);
  return crumb;
}

const appName = () => (location.pathname.startsWith("/admin") ? "admin" : "player");

/** Starts monitoring once the configuration is known. An empty DSN turns it off for this page. */
export function startMonitoring(options: { dsn: string; environment: string; release?: string }) {
  const stop = () => { off = true; waiting.length = 0; stopEarlyCapture(); };
  if (!options.dsn) return stop();
  import("./sentry").then((S) => {
    S.init({
      dsn: options.dsn,
      environment: options.environment,
      release: options.release || undefined,
      // Sentry's defaults collect user details, cookies, headers, query strings and request bodies: none of it here.
      dataCollection: { userInfo: false, cookies: false, httpHeaders: false, httpBodies: [], urlQueryParams: false, stackFrameVariables: false },
      // Errors only: release-health sessions would add a request to Sentry on every viewer's page load.
      integrations: (defaults) => defaults.filter((i) => i.name !== "BrowserSession"),
      beforeSend: (event) => prepareEvent(event, { ...tags }, appName()),
      beforeBreadcrumb: (crumb) => prepareBreadcrumb(crumb),
    });
    sentry = S;
    stopEarlyCapture();
    for (const run of waiting.splice(0)) run(S);
  }).catch(stop);
}

/** Labels later error reports with what the page is showing. A null, undefined or empty value removes the label. */
export function setMonitoringTags(next: Record<string, string | number | null | undefined>) {
  for (const [k, v] of Object.entries(next)) {
    if (v === null || v === undefined || v === "") delete tags[k];
    else tags[k] = String(v).slice(0, 200);
  }
}

/** Reports an error that was caught rather than thrown to the window (an error boundary, a test error). */
export function reportError(error: unknown, extra?: Record<string, unknown>) {
  whenReady((s) => s.captureException(error, extra ? { extra } : undefined));
}

/** False once it's known that this page has no monitoring (no DSN, or the SDK failed to load). */
export const monitoringOn = () => !off;
